import FadeInPanel from '@/shared/Animated/FadeInPanel'
import usePopup from '@/shared/hooks/usePopup'
import { IOption, Primitive } from '@/types/global'
import cn from '@/utils/cn'
import { CSSProperties, useMemo, useRef, useState } from 'react'
import css from './dropdownFilterChips.module.css'
import ArrowDown from '../../../../assets/icons/arrowDown.svg?react'
import Close from '../../../../assets/icons/close.svg?react'
import Button from '@/shared/Buttons/Button'
import DropdownInputListItem from '@/shared/Dropdowns/DropdownInputListItem'

interface IProps<T extends Primitive> {
  options: IOption<T>[]
  onSelect: (option: T, name: string) => void
  selectedOption: T

  panelStyle?: CSSProperties
  title?: string
  disabled?: boolean
  style?: CSSProperties
  name?: string
  loading?: boolean
  emptySelectionLabel?: string
}

const DropdownFilterChips = <T extends Primitive>({ emptySelectionLabel = 'Все', ...props }: IProps<T>) => {
  const [tempSelectedOption, setTempSelectedOption] = useState<T>(null)
  const ref = useRef<HTMLInputElement>()
  const { open, setOpen } = usePopup(ref)

  const handleSelect = (option: IOption<T>) => {
    setTempSelectedOption((prev) => {
      if (prev === option.value) return null
      return option.value
    })
  }

  const renderOptions = () => {
    return (
      <div className={css.options}>
        {props.options.map((option, index) => (
          <DropdownInputListItem
            key={index}
            onClick={(ev) => {
              ev.stopPropagation()
              handleSelect(option)
            }}
            active={tempSelectedOption === option.value}
          >
            <p className="text-nm">{option.title}</p>
          </DropdownInputListItem>
        ))}
      </div>
    )
  }

  const selectedOption = useMemo(
    () => props.options.find((o) => o.value === props.selectedOption),
    [props.selectedOption, props.options]
  )

  const renderInput = () => {
    const tip = !selectedOption ? emptySelectionLabel : selectedOption.title
    return (
      <div>
        <span data-filled={!!selectedOption} className={cn('text-nm line-clamp', css.inputText)}>
          {!selectedOption && props.title}
          <span className={cn(css.inputTextTip, 'text-heading-6')}>{tip}</span>
        </span>
      </div>
    )
  }

  const renderFooter = () => {
    if (props.loading) return null
    return (
      <div
        className={cn('flex-space-between', css.btnContainer)}
        onClick={(ev) => {
          ev.stopPropagation()
        }}
      >
        <Button
          size="default"
          type="tetriary"
          text="Сбросить"
          disabled={tempSelectedOption == null}
          onClick={() => {
            setTempSelectedOption(null), props.onSelect(null, props.name)
          }}
        />
        <Button
          size="default"
          type="primary"
          disabled={tempSelectedOption == null}
          text="Применить"
          onClick={() => {
            props.onSelect(tempSelectedOption, props.name), setOpen(false)
          }}
        />
      </div>
    )
  }

  const renderClose = () => {
    if (!selectedOption) return null
    return (
      <div
        className={css.closeIcon}
        onClick={(e) => {
          e.stopPropagation()
          setTempSelectedOption(null), props.onSelect(null, props.name)
        }}
      >
        <Close width={8.5} height={8.5} />
      </div>
    )
  }

  return (
    <div
      className={css.wrapper}
      style={props.style}
      data-filled={!!selectedOption}
      data-open={open}
      ref={ref}
      onClick={!props.disabled ? () => setOpen((prev) => !prev) : null}
    >
      {renderInput()}
      {!props.disabled && <ArrowDown data-open={open} data-filled={!!selectedOption} className={css.arrow} />}
      {renderClose()}
      <FadeInPanel open={open} className={css.fadeInPanel} style={props.panelStyle}>
        {renderOptions()}
        {renderFooter()}
      </FadeInPanel>
    </div>
  )
}

export default DropdownFilterChips
